import { Point } from 'pixi.js';
import { BREAKPOINT_XLARGE } from '../../styles/common/breakpoints';

// For viewports 1300px or larger, we use the larger Island background image
export const IMAGE_LARGE_BREAKPOINT = BREAKPOINT_XLARGE;

// Common stage options for the two Island renderers
export const stageOptions = {
  backgroundColor: 0xffffff,
  resolution: 1,
};

export interface Scale {
  x: number;
  y: number;
}

interface PixiDimensions {
  worldWidth: number;
  worldHeight: number;
  worldCentre: Point;
  screenWidth: number;
  screenHeight: number;
  islandImageWidth: number;
  islandImageHeight: number;
  marginHorizontal: number;
  marginVertical: number;
}

export function createPixiDimensions(width: number, height: number): PixiDimensions {
  const config = {
    worldWidth: 0,
    worldHeight: 0,
    worldCentre: new Point(undefined, undefined),
    screenWidth: 0,
    screenHeight: 0,
    islandImageWidth: 0,
    islandImageHeight: 0,
    marginHorizontal: 0,
    marginVertical: 0,
  };

  if (width > height && (width / height) >= (16 / 9)
    /**
     * If the height of the world based on the width won't fit on the screen, we need to calculate
     * the dimensions based on the height.
     */
    && ((width * 9) / 16) <= height) {
    /**
     * Width based calculations.
     */
    config.screenWidth = width;
    config.screenHeight = height;

    config.worldWidth = width;
    config.worldHeight = (config.worldWidth * 9) / 16;

    config.islandImageWidth = config.worldWidth * 2;
    config.islandImageHeight = config.islandImageWidth;

    config.marginHorizontal = ((config.islandImageWidth - config.worldWidth) / 2) / config.islandImageWidth;
    config.marginVertical = ((config.islandImageHeight - config.worldHeight) / 2) / config.islandImageHeight;
  } else {
    /**
     * Height based calculations
     */
    config.screenWidth = width;
    config.screenHeight = height;

    config.worldWidth = width;
    config.worldHeight = (config.worldWidth * 9) / 16;
    if (config.worldHeight > config.screenHeight) {
      /**
       * The world height is out of bounds and we need to readjust the world dimentions to fit.
       */
      const shrinkRatio = config.screenHeight / config.worldHeight;
      config.worldHeight = config.screenHeight;

      config.worldWidth *= shrinkRatio;
    }

    /**
     * Island image sizing
     */
    config.islandImageWidth = config.worldWidth * 2;
    config.islandImageHeight = config.islandImageWidth;

    if (config.islandImageHeight < config.screenHeight) {
      /**
       * If the rendered image will not fit the entire screen, we must re-adjust the values.
       * This scenario (rare) is relevant on devices which have a thin display.
       */

      /**
       * Since the screenHeight is larger, we establish by how much by the divison below.
       */
      const enlargeRatio = config.screenHeight / config.islandImageHeight;

      /**
       * Re-adjust all the other sizes. Since it is enlarged, we are now in a "zoomed in" state where
       * we actually cannot see the entire island at the same time.
       */
      config.islandImageWidth *= enlargeRatio;
      config.islandImageHeight *= enlargeRatio;
      config.worldHeight *= enlargeRatio;
      config.worldWidth *= enlargeRatio;
    }

    if (config.islandImageWidth < config.screenWidth) {
      /**
       * If the rendered image does not cover the screen width-wise, we must re-adjust the values.
       * This scenario (rare) is relevant on devices which have a thin display.
       */

      /**
       * Since the screenWidth is larger, we establish by how much we should enlarge the image.
       */
      const enlargeRatio = config.screenWidth / config.islandImageWidth;

      /**
       * Re-adjust all the other sizes. Since it is enlarged, we are now in a "zoomed in" state where
       * we actually cannot see the entire island at the same time.
       */
      config.islandImageWidth *= enlargeRatio;
      config.islandImageHeight *= enlargeRatio;
      config.worldHeight *= enlargeRatio;
      config.worldWidth *= enlargeRatio;
    }

    config.marginHorizontal = ((config.islandImageWidth - config.worldWidth) / 2) / config.islandImageWidth;
    config.marginVertical = ((config.islandImageHeight - config.worldHeight) / 2) / config.islandImageHeight;
  }

  config.worldCentre.set(config.worldWidth / 2, config.worldHeight / 2);

  return config;
}

/**
 * Turns a point location to a percentage based on the world dimension.
 * @param {number} coordinate x or y position
 * @param {number} worldDimension x or y max position
 * @returns {number}
 */
export const pointCoordinateToPercentage = (coordinate: number, worldDimension: number): number => (
  (coordinate * 100) / worldDimension
);

/**
 * Turns a percentage to a point coordinate based on the world dimension.
 * @param {number} worldDimension x or y max position
 * @param {number} coordinate x or y position
 * @returns {number}
 */
export const percentageToPointCoordinate = (percentage: number, worldDimension: number): number => (
  (percentage * worldDimension) / 100
);

export const loadImage = async (url: string): Promise<void> => new Promise((resolve) => {
  const image = new Image();
  image.onload = () => {
    resolve();
  };
  image.crossOrigin = 'anonymous';
  image.src = url;
});

export default {};
